import { useState, useEffect, useRef, KeyboardEvent } from "react";
import { Plus, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type AutosuggestInputProps = {
  type: "COMPLAINT" | "DIAGNOSIS" | "TREATMENT";
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onAddItem?: (item: string) => void;
  selectedItems?: string[];
  onRemoveItem?: (item: string) => void;
  shortcutKey?: string;
};

type Suggestion = {
  id: string;
  suggestion_text: string;
};

export function AutosuggestInput({
  type,
  value,
  onChange,
  placeholder,
  onAddItem,
  selectedItems = [],
  onRemoveItem,
  shortcutKey,
}: AutosuggestInputProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [suggestionToDelete, setSuggestionToDelete] = useState<Suggestion | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const componentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSuggestions();

    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && shortcutKey && event.key.toLowerCase() === shortcutKey.toLowerCase()) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown as any);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown as any);
    };
  }, [type, shortcutKey]);

  useEffect(() => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      setSelectedIndex(0);
      if (listRef.current) {
        const firstItem = listRef.current.querySelector('[data-index="0"]');
        if (firstItem) {
          firstItem.scrollIntoView({ block: 'nearest' });
        }
      }
    } else {
      setSelectedIndex(-1);
    }
  }, [showSuggestions, value]);

  const loadSuggestions = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("medical_suggestions")
        .select("id, suggestion_text")
        .eq("type", type)
        .eq("school_id", user?.schoolId)
        .order('usage_count', { ascending: false });

      if (error) throw error;
      setSuggestions(data || []);
    } catch (error) {
      console.error("Error loading suggestions:", error);
      toast({
        title: "Error",
        description: "Failed to load suggestions",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
  };

  const isDuplicate = (newItem: string, existingItems: string[]): boolean => {
    const normalizedNew = normalizeText(newItem);
    return existingItems.some(item => normalizeText(item) === normalizedNew);
  };

  const addNewSuggestion = async () => {
    if (!value.trim()) return;
    
    if (!user?.schoolId) {
      toast({
        title: "Error",
        description: "School ID not found",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: existingSuggestions } = await supabase
        .from("medical_suggestions")
        .select("id, suggestion_text")
        .eq("type", type)
        .eq("school_id", user.schoolId);

      const isDuplicateText = existingSuggestions?.some(
        suggestion => normalizeText(suggestion.suggestion_text) === normalizeText(value)
      );

      if (isDuplicateText) {
        const duplicateSuggestion = existingSuggestions?.find(
          suggestion => normalizeText(suggestion.suggestion_text) === normalizeText(value)
        );
        
        if (duplicateSuggestion && onAddItem) {
          onAddItem(duplicateSuggestion.suggestion_text);
          onChange("");
          setShowSuggestions(false);
          setSelectedIndex(-1);
          
          toast({
            title: "Info",
            description: "Similar suggestion already exists and was selected",
          });
          return;
        }
      }

      const { data: newSuggestion, error } = await supabase
        .from("medical_suggestions")
        .insert([
          {
            type,
            suggestion_text: value.trim(),
            school_id: user.schoolId,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setSuggestions(prev => [...prev, newSuggestion]);

      if (onAddItem) {
        onAddItem(value);
      }
      onChange("");
      setShowSuggestions(false);
      setSelectedIndex(-1);
      
      toast({
        title: "Success",
        description: "New suggestion added",
      });
    } catch (error) {
      console.error("Error adding suggestion:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const deleteSuggestion = async (suggestion: Suggestion) => {
    setSuggestionToDelete(suggestion);
  };

  const handleDeleteConfirm = async () => {
    if (!suggestionToDelete || !user?.schoolId) return;

    try {
      console.log('Deleting suggestion:', suggestionToDelete);
      
      const { error } = await supabase
        .from("medical_suggestions")
        .delete()
        .eq("id", suggestionToDelete.id)
        .eq("type", type)
        .eq("school_id", user.schoolId);

      if (error) {
        console.error('Delete error:', error);
        toast({
          title: "Error",
          description: "Failed to delete suggestion. " + error.message,
          variant: "destructive",
        });
        return;
      }

      setSuggestions(prev => prev.filter(s => s.id !== suggestionToDelete.id));
      
      if (value === suggestionToDelete.suggestion_text) {
        onChange("");
      }

      setShowSuggestions(false);

      toast({
        title: "Success",
        description: "Suggestion deleted successfully",
      });

    } catch (error) {
      console.error("Error deleting suggestion:", error);
      toast({
        title: "Error",
        description: "Failed to delete suggestion",
        variant: "destructive",
      });
    } finally {
      setSuggestionToDelete(null);
    }
  };

  const filteredSuggestions = suggestions.filter(
    (suggestion) =>
      suggestion.suggestion_text.toLowerCase().includes(value.toLowerCase()) &&
      !selectedItems.includes(suggestion.suggestion_text)
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Prevent arrow keys from scrolling the page when dropdown is open
    if (showSuggestions && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
    }

    if (showSuggestions && filteredSuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          const newIndex = e.key === 'ArrowDown'
            ? (selectedIndex < filteredSuggestions.length - 1 ? selectedIndex + 1 : 0)
            : (selectedIndex > 0 ? selectedIndex - 1 : filteredSuggestions.length - 1);
          
          setSelectedIndex(newIndex);
          
          const selectedElement = listRef.current?.querySelector(`[data-index="${newIndex}"]`);
          if (selectedElement) {
            selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
            handleItemSelect(filteredSuggestions[selectedIndex].suggestion_text);
          } else if (value.trim()) {
            addNewSuggestion();
          }
          break;

        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          setSelectedIndex(-1);
          break;

        case 'Tab':
          if (showSuggestions) {
            e.preventDefault();
            const nextIndex = selectedIndex < filteredSuggestions.length - 1 ? selectedIndex + 1 : 0;
            setSelectedIndex(nextIndex);
          }
          break;
      }
    } else if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      addNewSuggestion();
    }
  };

  const handleItemSelect = (suggestion_text: string) => {
    if (onAddItem) {
      onAddItem(suggestion_text);
      onChange("");
      setShowSuggestions(false);
      setSelectedIndex(-1);
      
      inputRef.current?.blur();
      
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  };

  const getShortcutText = () => {
    if (!shortcutKey) return '';
    return `(Alt + ${shortcutKey})`;
  };

  return (
    <TooltipProvider>
      <div className="space-y-2" ref={componentRef}>
        <div className="relative w-full">
          <div className="flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Input
                  ref={inputRef}
                  type="text"
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => {
                    setShowSuggestions(true);
                    if (value && filteredSuggestions.length > 0) {
                      setSelectedIndex(0);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowSuggestions(false);
                      setSelectedIndex(-1);
                    }, 150);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={`${placeholder || `Enter ${type.toLowerCase()}`} ${getShortcutText()}`}
                  className="w-full"
                />
              </TooltipTrigger>
              <TooltipContent className="bg-[#F1F0FB] border border-gray-200 shadow-lg p-3 rounded-lg">
                <p className="text-gray-700">Type to search or add new {type.toLowerCase()}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Use ↑↓ to navigate, Enter to select, Esc to dismiss
                </p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addNewSuggestion}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4" />
                  Add New
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-[#F1F0FB] border border-gray-200 shadow-lg p-3 rounded-lg">
                <p className="text-gray-700">Add a new {type.toLowerCase()} to the list</p>
                <p className="text-xs text-gray-500 mt-1">
                  Press Enter while typing to quick add
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          
          {showSuggestions && (
            <div className="absolute z-50 w-full mt-1">
              <div className="rounded-lg border shadow-md bg-popover text-popover-foreground">
                <div ref={listRef} className="max-h-[300px] overflow-y-auto p-1">
                  {filteredSuggestions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No suggestions found
                    </div>
                  ) : (
                    filteredSuggestions.map((suggestion, index) => (
                      <div
                        key={suggestion.id}
                        data-index={index}
                        onClick={() => handleItemSelect(suggestion.suggestion_text)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`cursor-pointer transition-colors duration-100 py-3 px-3 rounded-md flex justify-between items-center
                          ${index === selectedIndex 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-accent"
                          }
                        `}
                      >
                        <span>{suggestion.suggestion_text}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 hover:bg-destructive/20 ml-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSuggestion(suggestion);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete suggestion</span>
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {selectedItems.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedItems.map((item, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="flex items-center gap-1 px-3 py-1"
              >
                <span>{item}</span>
                {onRemoveItem && (
                  <button
                    type="button"
                    onClick={() => onRemoveItem(item)}
                    className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Remove {item}</span>
                  </button>
                )}
              </Badge>
            ))}
          </div>
        )}

        <AlertDialog 
          open={!!suggestionToDelete} 
          onOpenChange={(open) => !open && setSuggestionToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Suggestion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this suggestion? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
